/**
 * Cart Discount Code Handler - Web Component
 * Manages discount code UI interactions with the Shopify Cart API
 */

class CartDiscount extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Bind elements when component is added to DOM
    this.bindElements();
    
    // Restore discount code from session if exists
    this.restoreDiscount();
    
    // Update checkout button with discount code
    this.setupCheckoutButton();
  }

  disconnectedCallback() {
    // Clean up when component is removed
    this.unbindElements();
  }

  bindElements() {
    // Toggle promo form
    this.promoToggle = this.querySelector('[data-promo-toggle]');
    if (this.promoToggle) {
      this.promoToggle.addEventListener('click', this.showPromoForm.bind(this));
    }

    // Close promo form
    this.promoClose = this.querySelector('[data-promo-close]');
    if (this.promoClose) {
      this.promoClose.addEventListener('click', this.hidePromoForm.bind(this));
    }

    // Handle discount apply button click
    this.applyButton = this.querySelector('[data-promo-apply]');
    if (this.applyButton) {
      this.applyButton.addEventListener('click', this.handleDiscountApply.bind(this));
    }

    // Clear error on input
    this.promoInput = this.querySelector('[data-promo-input]');
    if (this.promoInput) {
      this.promoInput.addEventListener('input', this.clearError.bind(this));
    }

    // Handle discount removal
    this.removeButtons = this.querySelectorAll('[data-discount-code]');
    this.removeButtons.forEach(button => {
      button.addEventListener('click', this.deleteDiscount.bind(this));
    });
  }

  unbindElements() {
    if (this.promoToggle) {
      this.promoToggle.removeEventListener('click', this.showPromoForm.bind(this));
    }
    if (this.promoClose) {
      this.promoClose.removeEventListener('click', this.hidePromoForm.bind(this));
    }
    if (this.applyButton) {
      this.applyButton.removeEventListener('click', this.handleDiscountApply.bind(this));
    }
    if (this.promoInput) {
      this.promoInput.removeEventListener('input', this.clearError.bind(this));
    }
    if (this.removeButtons) {
      this.removeButtons.forEach(button => {
        button.removeEventListener('click', this.deleteDiscount.bind(this));
      });
    }
  }

  showPromoForm() {
    const button = this.querySelector('[data-promo-toggle]');
    const form = this.querySelector('[data-promo-form]');
    
    if (button && form) {
      button.style.display = 'none';
      form.style.display = 'flex';
      
      // Focus the input
      const input = form.querySelector('[data-promo-input]');
      if (input) {
        setTimeout(() => input.focus(), 100);
      }
    }
  }

  hidePromoForm() {
    const button = this.querySelector('[data-promo-toggle]');
    const form = this.querySelector('[data-promo-form]');
    
    if (button && form) {
      button.style.display = 'flex';
      form.style.display = 'none';
      this.clearError();
    }
  }

  async handleDiscountApply(e) {
    e.preventDefault();
    e.stopPropagation();
    
    const input = this.querySelector('[data-promo-input]');
    const code = input ? input.value.trim().toUpperCase() : '';


    if (!code) {
      return;
    }

    // Clear previous error
    this.clearError();
    
    // Call Shopify Cart API to apply discount
    try {
      // Disable apply button while request is in-flight
      if (this.applyButton) {
        this.applyButton.setAttribute('disabled', 'disabled');
        this.applyButton.setAttribute('aria-busy', 'true');
      }
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: code
        })
      });
      
      const data = await response.json();
      
      // Check if discount was successfully applied
      const discountApplied = data.discount_codes && 
                              data.discount_codes.length > 0 && 
                              data.discount_codes[0].applicable === true;

      if (response.ok && discountApplied) {
        const appliedCode = data.discount_codes[0].code;

        // Refresh cart sections first so totals and discounts render together
        await this.refreshCartSections();

        // Store discount code in session storage
        sessionStorage.setItem('shopify_discount_code', appliedCode);

        // Update the cart UI to show the applied discount (after sections refreshed)
        //this.showAppliedState(appliedCode);
      } else {
        this.showError('Sorry, that is not a valid code');
        input.classList.add('has-error');
      }
      
    } catch {
      // Network/parse failure is surfaced to the shopper via the inline error message
      this.showError('Failed to apply discount code');
      input.classList.add('has-error');
    } finally {
      // Re-enable apply button unless the UI replaced it
      if (this.applyButton) {
        this.applyButton.removeAttribute('disabled');
        this.applyButton.removeAttribute('aria-busy');
      }
    }
  }

  showError(message) {
    const errorEl = this.querySelector('[data-promo-error]');
    const messageEl = this.querySelector('[data-error-message]');
    
    if (errorEl && messageEl) {
      messageEl.textContent = message;
      errorEl.style.display = 'flex';
    }
  }

  clearError() {
    const input = this.querySelector('[data-promo-input]');
    const errorEl = this.querySelector('[data-promo-error]');
    
    if (input) {
      input.classList.remove('has-error');
    }
    
    if (errorEl) {
      errorEl.style.display = 'none';
    }
  }

  async restoreDiscount() {
    // Fetch current cart state from Shopify to check for existing discount codes
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Check if cart has discount codes applied
      if (cart.discount_codes && cart.discount_codes.length > 0) {
        const appliedDiscount = cart.discount_codes.find(d => d.applicable === true);
        if (appliedDiscount) {
          sessionStorage.setItem('shopify_discount_code', appliedDiscount.code);
          this.showAppliedState(appliedDiscount.code);
        }
      }
    } catch (error) {
      // Silent error
    }
  }

  setupCheckoutButton() {
    // Check line item quantities and disable button if any qty > 5
    this.checkAndUpdateCheckoutButton();
    
    // Find all checkout buttons and update their href with discount code
    const checkoutButtons = document.querySelectorAll('.cart-checkout-button, .cart--button[name="checkout"]');
    
    checkoutButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        // If button is disabled due to qty > 5, prevent checkout
        if (button.hasAttribute('disabled')) {
          e.preventDefault();
          return;
        }
        
        const discountCode = sessionStorage.getItem('shopify_discount_code');
        if (discountCode) {
          e.preventDefault();
          window.location.href = `/checkout?discount=${encodeURIComponent(discountCode)}`;
        }
        // If no discount code, allow normal form submission
      });
    });
  }

  /**
   * Check cart line items and disable checkout button if any qty > 5
   * Optimized: Single async call to check all items and update UI once
   */
  async checkAndUpdateCheckoutButton() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Check if any line item has quantity > 5
      const hasExceededQty = cart.items && cart.items.some(item => item.quantity > 5);
      
      // Get all checkout buttons
      const checkoutButtons = document.querySelectorAll('.cart-checkout-button, .cart--button[name="checkout"]');
      
      // Update disabled state based on quantity check
      checkoutButtons.forEach(button => {
        if (hasExceededQty) {
          button.setAttribute('disabled', 'disabled');
          button.setAttribute('aria-disabled', 'true');
          button.title = 'Checkout disabled: Maximum quantity per item is 5';
        } else {
          button.removeAttribute('disabled');
          button.removeAttribute('aria-disabled');
          button.title = '';
        }
      });
      
    } catch (error) {
      // Silent error - allow checkout to proceed
      console.error('Error checking cart items:', error);
    }
  }

  showAppliedState(code) {
    // Hide the form wrapper and show applied state
    const wrapper = this.querySelector('[data-promo-wrapper]');
    if (wrapper) {
      wrapper.style.display = 'none';
    }

    // Create or update applied discount UI
    let appliedDiv = this.querySelector('.cart-promo-applied');
    if (!appliedDiv) {
      appliedDiv = document.createElement('div');
      appliedDiv.className = 'cart-promo-applied';
      this.appendChild(appliedDiv);
    }

    appliedDiv.innerHTML = `
      <span>Added Promo or Discount: <span id="applied-discount-code">${code}</span></span>
      <button type="button" 
              class="cart-promo-remove" 
              data-discount-code="${code}"
              aria-label="Remove discount">
        <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7.82679 5.00035L7.59612 11.0003M4.40412 11.0003L4.17346 5.00035M10.8188 2.86035C11.0468 2.89501 11.2735 2.93168 11.5001 2.97101M10.8188 2.86035L10.1068 12.1157C10.0777 12.4925 9.9075 12.8445 9.63014 13.1012C9.35277 13.3579 8.98873 13.5005 8.61079 13.5003H3.38946C3.01152 13.5005 2.64747 13.3579 2.37011 13.1012C2.09275 12.8445 1.92251 12.4925 1.89346 12.1157L1.18146 2.86035M10.8188 2.86035C10.0494 2.74403 9.27597 2.65574 8.50012 2.59568M1.18146 2.86035C0.953455 2.89435 0.726789 2.93101 0.500122 2.97035M1.18146 2.86035C1.95088 2.74403 2.72428 2.65575 3.50012 2.59568M8.50012 2.59568V1.98501C8.50012 1.19835 7.89346 0.542348 7.10679 0.517681C6.3692 0.494106 5.63105 0.494106 4.89346 0.517681C4.10679 0.542348 3.50012 1.19901 3.50012 1.98501V2.59568M8.50012 2.59568C6.83594 2.46707 5.16431 2.46707 3.50012 2.59568" stroke="#161716" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    `;

    // Re-attach remove button listener
    const removeBtn = appliedDiv.querySelector('.cart-promo-remove');
    if (removeBtn) {
      removeBtn.addEventListener('click', (e) => this.deleteDiscount(e));
    }
  }
  async refreshCartSections() {
    try {
      // Determine which cart sections to refresh based on current page
      const sections = window.location.pathname === '/cart' 
        ? ['cart-desktop', 'cart-mobile']
        : ['cart-drawer'];
      
      // Fetch updated cart sections
      const response = await fetch(`${window.Shopify.routes.cart_update}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sections: sections
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch cart sections');
      }
      
      const data = await response.json();      
      
      // Update each cart section with the new HTML
      if (data.sections) {
        Object.entries(data.sections).forEach(([sectionName, sectionHTML]) => {
          // Parse the HTML string
          const parser = new DOMParser();
          const doc = parser.parseFromString(sectionHTML, 'text/html');
          const newContent = doc.querySelector('.cart--form');
          
          if (newContent) {
            // Find the corresponding cart form in the DOM
            const cartElement = document.querySelector(`cart-element[data-view="${sectionName.replace('cart-', '')}"]`);
            if (cartElement) {
              const currentForm = cartElement.querySelector('.cart--form');
              if (currentForm) {
                // Replace the old form with the new one
                currentForm.innerHTML = newContent.innerHTML;
              }
            }
          }
        });
        
        // Check and update checkout button state after sections refreshed
        await this.checkAndUpdateCheckoutButton();
      }
    } catch (error) {
      // Silent error
    }
  }

  async deleteDiscount(event) {    
    // Determine source button (if clicked) and disable it
    const sourceBtn = event?.currentTarget || this.querySelector('.cart-promo-remove');
    this.setDiscountButtonBusy(sourceBtn, true);
    
    // Call Shopify Cart API to remove discount
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: ''
        })
      });

      if (response.ok) {
        // Refresh cart sections first so totals and discounts render together
        await this.refreshCartSections();

        // Remove from session storage
        sessionStorage.removeItem('shopify_discount_code');

        // Hide applied state and show form again (if still present)
        const appliedDiv = this.querySelector('.cart-promo-applied');
        if (appliedDiv) {
          appliedDiv.remove();
        }

        const wrapper = this.querySelector('[data-promo-wrapper]');
        if (wrapper) {
          wrapper.style.display = 'block';
        }

        // Reset form
        const input = this.querySelector('[data-promo-input]');
        if (input) {
          input.value = '';
        }
        this.hidePromoForm();
      } else if (sourceBtn) {
        // Re-enable source button on failure
        sourceBtn.removeAttribute('disabled');
        sourceBtn.removeAttribute('aria-busy');
      }
      
    } catch {
      // Silent error - button is restored so the shopper can retry
      if (sourceBtn) {
        sourceBtn.removeAttribute('disabled');
        sourceBtn.removeAttribute('aria-busy');
      }
    }
  }

  setDiscountButtonBusy(button, isBusy) {
    if (!button) {
      return;
    }

    if (isBusy) {
      button.setAttribute('disabled', 'disabled');
      button.setAttribute('aria-busy', 'true');
      return;
    }

    button.removeAttribute('disabled');
    button.removeAttribute('aria-busy');
  }

  async completeDiscountRemoval() {
    // Refresh cart sections first so totals and discounts render together
    await this.refreshCartSections();

    sessionStorage.removeItem('shopify_discount_code');

    const appliedDiv = this.querySelector('.cart-promo-applied');
    if (appliedDiv) {
      appliedDiv.remove();
    }

    const wrapper = this.querySelector('[data-promo-wrapper]');
    if (wrapper) {
      wrapper.style.display = 'block';
    }

    if (window.recalculatePaymentButtons) {
      window.recalculatePaymentButtons();
    }

    const input = this.querySelector('[data-promo-input]');
    if (input) {
      input.value = '';
    }
    this.hidePromoForm();
  }
}

// Register the custom element
customElements.define('cart-discount', CartDiscount);

// payment button alignment button for mobile
(() => {
    const selector =
        ".dynamic-checkout__content shopify-accelerated-checkout-cart";

    const tags = [
        "SHOP-PAY-WALLET-BUTTON",
        "SHOPIFY-APPLE-PAY-BUTTON",
        "SHOPIFY-PAYPAL-BUTTON",
        "SHOPIFY-GOOGLE-PAY-BUTTON"
    ];

    const GAP = 8;
    const HEIGHT = 45;

    let timer, running;

    const css = (el, styles) =>
        Object.entries(styles).forEach(([k, v]) =>
            el.style.setProperty(k, v, "important")
        );

    function applyLayout() {
        if (running) return;

        const checkout = document.querySelector(selector);
        if (!checkout) return;

        const buttons = [...checkout.children].filter(el =>
            tags.includes(el.tagName)
        );

        if (!buttons.length) return;

        running = true;

        css(checkout, {
            position: "relative",
            display: "block",
            width: "100%",
            overflow: "visible"
        });

        buttons.forEach(btn =>
            css(btn, {
                position: "relative",
                width: "100%",
                height: `${HEIGHT}px`,
                margin: "0",
                padding: "0",
                transform: "none",
                "min-width": "0",
                "max-width": "none",
                "box-sizing": "border-box"
            })
        );

        requestAnimationFrame(() => {
            const rect = checkout.getBoundingClientRect();
            const count = buttons.length;

            if (!rect.width) {
                running = false;
                return;
            }

            const colWidth =
                count === 4
                    ? (rect.width - GAP) / 2
                    : (rect.width - GAP * (count - 1)) / count;

            buttons.forEach((btn, i) => {
                const b = btn.getBoundingClientRect();

                const x =
                    count === 4
                        ? (i % 2) * (colWidth + GAP)
                        : i * (colWidth + GAP);

                const y =
                    count === 4
                        ? Math.floor(i / 2) * (HEIGHT + GAP)
                        : 0;

                css(btn, {
                    width: `${colWidth}px`,
                    transform: `translate3d(${
                        x - (b.left - rect.left)
                    }px, ${
                        y - (b.top - rect.top)
                    }px, 0)`
                });
            });

            checkout.style.setProperty(
                "height",
                `${count === 4 ? HEIGHT * 2 + GAP : HEIGHT}px`,
                "important"
            );

            running = false;
        });
    }

    const schedule = () => {
        clearTimeout(timer);
        timer = setTimeout(applyLayout, 100);
    };

    applyLayout();

    window.addEventListener("resize", schedule, {
        passive: true
    });

    // Export schedule function globally for manual calls from cart quantity handlers
    window.recalculatePaymentButtons = schedule;

    // Recalculate on page load
    window.addEventListener("load", schedule);

    // Watch for drawer open (aria-expanded="true" on drawer--side)
    const drawerElement = document.querySelector('drawer-element');
    if (drawerElement) {
        const drawers = drawerElement.querySelectorAll('.drawer--side');
        drawers.forEach(drawer => {
            new MutationObserver((mutations) => {
                mutations.forEach(mutation => {
                    if (mutation.attributeName === 'aria-expanded' && 
                        drawer.getAttribute('aria-expanded') === 'true') {
                        schedule();
                    }
                });
            }).observe(drawer, { attributes: true, attributeFilter: ['aria-expanded'] });
        });
    }

    const container = document.querySelector(
        ".dynamic-checkout__content"
    );

    if (container) {
        // Only watch for direct payment button children changes, not entire subtree
        new MutationObserver(schedule).observe(container, {
            childList: true,
            subtree: false
        });
    }
})();